import { FadeIn } from 'components/common/FadeIn';
import PlusRing from 'components/svg/PlusRing.svg';
import TealRadio from 'components/svg/TealRadio.svg';
import { MainDrawDay } from 'constants/Draw';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { formattedPriceStringFromPence } from 'utils/common';
import styles from './DrawDaysSelector.module.css';
import HeadingAndSubheading from './HeadingAndSubheading';
import EntryWrapper, { EntryContent } from './OtherEntry';

interface DrawDaysSelectorProps {
  nextDrawDay: MainDrawDay;
}

const DRAW_OPTIONS = (
  costPerWeek: number | undefined,
  nextDrawDay: MainDrawDay,
) => [
  {
    label: nextDrawDay === MainDrawDay.TUESDAY ? 'Tuesday' : 'Friday',
    value: [nextDrawDay],
    info: 'Included',
  },
  {
    label: 'Tuesday & Friday',
    value: [MainDrawDay.TUESDAY, MainDrawDay.FRIDAY],
    info: costPerWeek
      ? `+${formattedPriceStringFromPence(costPerWeek)}/wk`
      : undefined,
  },
];

/** Selects the weekly draw days. At least one draw day is always kept selected. */
const DrawDaysSelector = observer(({ nextDrawDay }: DrawDaysSelectorProps) => {
  const enterStore = useEnterStore();
  const numEntries = enterStore.num_entries || enterStore.lines.length;
  const pricePerEntry =
    enterStore.plan?.priceTable[enterStore.plan.priceVersion]?.GBP[numEntries];
  const costPerWeek =
    pricePerEntry === undefined ? undefined : pricePerEntry * numEntries;

  return (
    <div className={styles.container}>
      <HeadingAndSubheading
        heading="More days made"
        subheading="When do you want to play?"
      />
      <div className={styles.dayOptions}>
        {DRAW_OPTIONS(costPerWeek, nextDrawDay).map((dayOption, i) => {
          const checked =
            dayOption.value.length === enterStore.days.length &&
            dayOption.value.every((d) => enterStore.days.includes(d));

          return (
            <FadeIn
              onScroll
              duration={0.3}
              delay={i * 0.05}
              key={dayOption.label}
            >
              <EntryWrapper
                onClick={() => enterStore.setDays(dayOption.value)}
                selected={checked}
              >
                <EntryContent
                  icon={<PlusRing />}
                  iconOnSelected={<TealRadio />}
                  textTitle={dayOption.label}
                  rightContent={
                    dayOption.info ? (
                      <span className="daymadeTypographySRegular textLightGrey">
                        {dayOption.info}
                      </span>
                    ) : (
                      <span role="status" aria-label="Loading weekly price">
                        <Skeleton width={72} height={16} />
                      </span>
                    )
                  }
                  selected={checked}
                />
              </EntryWrapper>
            </FadeIn>
          );
        })}
      </div>
    </div>
  );
});

export default DrawDaysSelector;
